<!--
 * @Author: LiZhiWei
 * @Date: 2026-08-12 10:00:00
 * @Description: 站点公共头部：品牌栏 + 一级导航 + 移动端抽屉
-->
<template>
  <header class="site-header" @keydown.escape="closeAll">
    <div class="header-tools">
      <div class="page-container tool-row">
        <nuxt-link class="brand" to="/" aria-label="返回海南兴科首页" @click="closeAll">
          <img
            class="brand-logo"
            src="/logo/xingke-logo.png"
            alt="海南兴科品牌标志"
            width="47"
            height="47"
          />
          <span class="brand-copy">
            <span class="brand-cn">{{ companyIdentity.name }}</span>
            <span class="brand-en">{{ companyIdentity.nameEn }}</span>
          </span>
        </nuxt-link>

        <div class="header-actions" aria-label="网站工具">
          <search-command>
            <button class="header-action" type="button">
              <i class="i-custom-search header-action-icon" aria-hidden="true"></i>
              <span>全站搜索</span>
            </button>
          </search-command>
          <contact-dialog>
            <button class="header-action" type="button">
              <i class="i-custom-phone header-action-icon" aria-hidden="true"></i>
              <span>联系我们</span>
            </button>
          </contact-dialog>
        </div>

        <button
          class="mobile-menu-button"
          type="button"
          :aria-label="mobileOpen ? '关闭导航菜单' : '打开导航菜单'"
          aria-controls="mobile-drawer"
          :aria-expanded="mobileOpen"
          @click="toggleMobile"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </div>

    <div class="nav-band">
      <div class="page-container nav-groups">
        <template v-for="group in navigationGroups" :key="group.key">
          <nav
            class="desktop-nav"
            :aria-label="group.label"
            :style="{ flexGrow: group.items.length }"
          >
            <div
              v-for="(item, index) in group.items"
              :key="item.label"
              class="nav-item"
              :class="{ active: isActive(item), open: openMenu === item.label }"
              @mouseenter="openMenu = item.children ? item.label : null"
              @mouseleave="openMenu = null"
            >
              <button
                v-if="item.children"
                class="nav-trigger"
                type="button"
                :aria-controls="`dropdown-${group.key}-${index}`"
                :aria-expanded="openMenu === item.label"
                :aria-current="isActive(item) ? 'page' : undefined"
                @click="toggleMenu(item.label)"
              >
                <span class="nav-label">{{ item.label }}</span>
                <span class="chevron" aria-hidden="true"></span>
              </button>
              <nuxt-link
                v-else
                class="nav-link"
                :to="item.to"
                :target="item.external ? item.target : undefined"
                :rel="item.external ? 'noopener' : undefined"
                :aria-current="isActive(item) ? 'page' : undefined"
                @click="closeAll"
              >
                <span class="nav-label">{{ item.label }}</span>
                <span v-if="item.external" class="external-mark" aria-hidden="true">↗</span>
              </nuxt-link>

              <div v-if="item.children" :id="`dropdown-${group.key}-${index}`" class="dropdown">
                <nuxt-link
                  v-for="child in item.children"
                  :key="child.to"
                  class="dropdown-link"
                  :class="{ active: isChildActive(child) }"
                  :to="child.to"
                  :aria-current="isChildActive(child) ? 'page' : undefined"
                  @click="closeAll"
                >
                  {{ child.label }}
                </nuxt-link>
              </div>
            </div>
          </nav>
          <nav
            v-if="group.key === 'primary' && mediaNav.length > 0"
            class="desktop-media-nav"
            aria-label="媒体分类"
            :style="{ flexGrow: mediaNav.length }"
          >
            <div
              v-for="(item, index) in mediaNav"
              :key="item.label"
              class="nav-item"
              :class="{ active: isActive(item), open: openMenu === `media-${item.label}` }"
              @mouseenter="openMenu = item.children ? `media-${item.label}` : null"
              @mouseleave="openMenu = null"
            >
              <button
                v-if="item.children"
                class="nav-trigger"
                type="button"
                :aria-controls="`media-dropdown-${index}`"
                :aria-expanded="openMenu === `media-${item.label}`"
                :aria-current="isActive(item) ? 'page' : undefined"
                @click="toggleMenu(`media-${item.label}`)"
              >
                <span class="nav-label">{{ item.label }}</span>
                <span class="chevron" aria-hidden="true"></span>
              </button>
              <nuxt-link
                v-else
                class="nav-link"
                :to="item.to"
                :aria-current="isActive(item) ? 'page' : undefined"
                @click="closeAll"
              >
                <span class="nav-label">{{ item.label }}</span>
              </nuxt-link>

              <div v-if="item.children" :id="`media-dropdown-${index}`" class="dropdown">
                <nuxt-link
                  v-for="child in item.children"
                  :key="child.to"
                  class="dropdown-link"
                  :class="{ active: isChildActive(child) }"
                  :to="child.to"
                  :aria-current="isChildActive(child) ? 'page' : undefined"
                  @click="closeAll"
                >
                  {{ child.label }}
                </nuxt-link>
              </div>
            </div>
          </nav>
        </template>
      </div>
    </div>

    <div v-show="mobileOpen" id="mobile-drawer" class="mobile-drawer">
      <div class="mobile-tool-actions">
        <search-command>
          <button class="mobile-tool-action" type="button" @click="closeMobile">
            <i class="i-custom-search header-action-icon" aria-hidden="true"></i>
            <span>全站搜索</span>
          </button>
        </search-command>
        <contact-dialog>
          <button class="mobile-tool-action" type="button" @click="closeMobile">
            <i class="i-custom-phone header-action-icon" aria-hidden="true"></i>
            <span>联系我们</span>
          </button>
        </contact-dialog>
      </div>

      <template v-for="group in navigationGroups" :key="group.key">
        <div
          v-for="item in group.items"
          :key="item.label"
          class="mobile-nav-row"
          :class="{ active: isActive(item) }"
        >
          <div class="mobile-nav-main">
            <nuxt-link
              v-if="item.to && !item.external"
              class="mobile-nav-link"
              :to="item.to"
              :aria-current="isActive(item) ? 'page' : undefined"
              @click="closeAll"
            >
              {{ item.label }}
            </nuxt-link>
            <nuxt-link
              v-else-if="item.external"
              class="mobile-nav-link"
              :to="item.to"
              target="_blank"
              rel="noopener"
              @click="closeAll"
            >
              {{ item.label }}
              <span class="external-mark" aria-hidden="true">↗</span>
            </nuxt-link>
            <button
              v-else
              class="mobile-nav-trigger"
              type="button"
              :aria-expanded="openSub === item.label"
              :aria-current="isActive(item) ? 'page' : undefined"
              @click="toggleSub(item.label)"
            >
              {{ item.label }}
              <span
                class="chevron"
                :class="{ open: openSub === item.label }"
                aria-hidden="true"
              ></span>
            </button>
          </div>

          <div v-if="item.children" class="mobile-subnav" :class="{ open: openSub === item.label }">
            <nuxt-link
              v-for="child in item.children"
              :key="child.to"
              class="mobile-subnav-link"
              :class="{ active: isChildActive(child) }"
              :to="child.to"
              :aria-current="isChildActive(child) ? 'page' : undefined"
              @click="closeAll"
            >
              {{ child.label }}
            </nuxt-link>
          </div>
        </div>
        <nav
          v-if="group.key === 'primary' && mediaNav.length > 0"
          class="mobile-media-nav"
          aria-label="媒体分类"
        >
          <div
            v-for="(item, index) in mediaNav"
            :key="item.label"
            class="mobile-nav-row"
            :class="{ active: isActive(item) }"
          >
            <div class="mobile-nav-main">
              <nuxt-link
                v-if="item.to && !item.external"
                class="mobile-nav-link"
                :to="item.to"
                :aria-current="isActive(item) ? 'page' : undefined"
                @click="closeAll"
              >
                {{ item.label }}
              </nuxt-link>
              <button
                v-else
                class="mobile-nav-trigger"
                type="button"
                :aria-controls="`mobile-media-subnav-${index}`"
                :aria-expanded="openSub === `media-${item.label}`"
                :aria-current="isActive(item) ? 'page' : undefined"
                @click="toggleSub(`media-${item.label}`)"
              >
                {{ item.label }}
                <span
                  class="chevron"
                  :class="{ open: openSub === `media-${item.label}` }"
                  aria-hidden="true"
                ></span>
              </button>
            </div>
            <div
              v-if="item.children"
              :id="`mobile-media-subnav-${index}`"
              class="mobile-subnav"
              :class="{ open: openSub === `media-${item.label}` }"
              :inert="openSub !== `media-${item.label}`"
            >
              <nuxt-link
                v-for="child in item.children"
                :key="child.to"
                class="mobile-subnav-link"
                :class="{ active: isChildActive(child) }"
                :to="child.to"
                :aria-current="isChildActive(child) ? 'page' : undefined"
                @click="closeAll"
              >
                {{ child.label }}
              </nuxt-link>
            </div>
          </div>
        </nav>
      </template>
    </div>
  </header>
</template>

<script setup lang="ts">
  import type { NavChild, NavItem } from '~/utils/site'

  import { getArticleCategoryRoute, getMediaCategoryEntryRoute } from '~/utils/content'
  import { companyIdentity, siteNav } from '~/utils/site'

  defineOptions({
    name: 'MHeader',
  })
  const route = useRoute()
  const { data: categoryResponse } = useArticleCategories()
  const articleNav = computed<NavItem[]>(() =>
    (categoryResponse.value?.data ?? []).map((category) => {
      const categoryRoute = getArticleCategoryRoute(category)
      const children = category.children.map((child) => {
        const childRoute = getArticleCategoryRoute(child)
        return { label: child.name, to: childRoute.path }
      })
      return children.length > 0
        ? { label: category.name, children }
        : { label: category.name, to: categoryRoute.path }
    })
  )
  const { categoryTree: mediaCategoryTree } = useMediaCategories()
  const mediaNav = computed<NavItem[]>(() =>
    mediaCategoryTree.value.map((category) => {
      const children = (category.children ?? []).map((child) => ({
        label: child.name,
        to: `/media/${encodeURIComponent(child.code)}`,
      }))
      return children.length > 0
        ? { label: category.name, children }
        : { label: category.name, to: getMediaCategoryEntryRoute(category).path }
    })
  )
  const navigationGroups = computed(() => {
    const mallIndex = siteNav.findIndex((item) => item.to === '/mall')
    const insertAt = mallIndex === -1 ? siteNav.length : mallIndex
    return [
      {
        key: 'primary',
        label: '一级导航',
        items: [...siteNav.slice(0, insertAt), ...articleNav.value],
      },
      { key: 'commerce', label: '商城与外部导航', items: siteNav.slice(insertAt) },
    ].filter((group) => group.items.length > 0)
  })
  const mobileOpen = ref(false)
  const openMenu = ref<null | string>(null)
  const openSub = ref<null | string>(null)
  const desktopBreakpoint = 768

  function pathOf(to: string) {
    return to.split('?')[0] ?? ''
  }

  function isPathActive(path: string) {
    return route.path === path || route.path.startsWith(`${path}/`)
  }

  function isChildActive(child: NavChild) {
    const [path, queryString] = child.to.split('?')
    if (!path || !isPathActive(path)) return false
    if (!queryString) return true

    const params = new URLSearchParams(queryString)
    return [...params.entries()].every(([key, value]) => {
      const currentValue = route.query[key]
      return Array.isArray(currentValue) ? currentValue.includes(value) : currentValue === value
    })
  }

  function isActive(item: NavItem) {
    if (item.external) return false
    if (item.to === '/') return false
    if (item.children) return item.children.some((child) => isPathActive(pathOf(child.to)))
    return item.to ? isPathActive(pathOf(item.to)) : false
  }

  function toggleMobile() {
    mobileOpen.value = !mobileOpen.value
    if (!mobileOpen.value) openSub.value = null
  }

  function closeMobile() {
    mobileOpen.value = false
    openSub.value = null
  }

  function closeAll() {
    mobileOpen.value = false
    openMenu.value = null
    openSub.value = null
  }

  function toggleMenu(label: string) {
    openMenu.value = openMenu.value === label ? null : label
  }

  function toggleSub(label: string) {
    openSub.value = openSub.value === label ? null : label
  }

  watch(
    () => route.path,
    () => closeAll()
  )

  if (import.meta.client) {
    useEventListener(window, 'resize', () => {
      if (window.innerWidth >= desktopBreakpoint) mobileOpen.value = false
    })
  }

  watch(mobileOpen, (open) => {
    if (import.meta.client) {
      document.body.style.overflow = open ? 'hidden' : ''
    }
  })

  onBeforeUnmount(() => {
    if (import.meta.client) {
      document.body.style.overflow = ''
    }
  })
</script>

<style scoped lang="scss">
  .site-header {
    position: sticky;
    top: 0;
    z-index: 40;
    background: var(--surface);
    box-shadow: 0 5px 18px rgb(37 37 37 / 8%);
  }

  .header-tools {
    height: 74px;
    background: var(--surface);
    border-bottom: 1px solid var(--line);
  }

  .tool-row {
    display: flex;
    gap: 24px;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

  .brand {
    display: flex;
    gap: 10px;
    align-items: center;
    min-width: 0;
    color: var(--ink);
  }

  .brand-logo {
    flex: 0 0 47px;
    width: 47px;
    height: 47px;
    object-fit: contain;
    background: #fff;
    border-radius: 50%;
  }

  .brand-cn {
    display: block;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: 0.01em;
  }

  .brand-en {
    display: block;
    margin-top: 4px;
    font-size: clamp(8px, 2.2vw, 9px);
    line-height: 1.1;
    color: var(--ink-muted);
    letter-spacing: 0.05em;
    white-space: nowrap;
  }

  .header-actions {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .header-action {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    min-height: 40px;
    padding: 8px 14px;
    color: var(--ink-muted);
    cursor: pointer;
    background: transparent;
    border: 0;
    border-left: 1px solid var(--line);
    transition: color 180ms ease;
  }

  .header-action:hover {
    color: var(--brand);
  }

  .header-action-icon {
    flex: 0 0 18px;
    width: 18px;
    height: 18px;
  }

  .nav-band {
    height: 52px;
    background: var(--brand);
  }

  .nav-groups {
    display: flex;
    align-items: stretch;
    height: 100%;
  }

  .desktop-nav,
  .desktop-media-nav {
    display: flex;
    flex: 1 1 0%;
    align-items: stretch;
    min-width: 0;
    height: 52px;
  }

  .nav-item {
    position: relative;
    display: flex;
    flex: 1 1 0%;
    align-items: center;
    min-width: 0;
  }

  .nav-item:hover,
  .nav-item.active,
  .nav-item.open {
    background: var(--brand-strong);
  }

  .nav-item.active::after {
    position: absolute;
    right: 22%;
    bottom: 0;
    left: 22%;
    height: 3px;
    content: '';
    background: #fff;
  }

  .nav-trigger,
  .nav-link {
    display: inline-flex;
    gap: 5px;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 0 10px;
    overflow: hidden;
    font-size: 14px;
    font-weight: 540;
    color: #fff;
    white-space: nowrap;
    cursor: pointer;
    background: none;
    border: 0;
  }

  .nav-label {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: center;
    white-space: nowrap;
  }

  .chevron {
    width: 7px;
    height: 7px;
    border-right: 1.5px solid currentColor;
    border-bottom: 1.5px solid currentColor;
    transform: rotate(45deg) translateY(-2px);
    transition: transform 180ms ease;
  }

  .nav-item.open .chevron {
    transform: rotate(225deg) translateY(0);
  }

  .chevron.open {
    transform: rotate(225deg) translateY(0);
  }

  .dropdown {
    position: absolute;
    top: 100%;
    left: 50%;
    visibility: hidden;
    width: 220px;
    padding: 10px;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 0 0 8px 8px;
    box-shadow: 0 18px 46px rgb(91 44 48 / 12%);
    opacity: 0;
    transform: translate(-50%, 8px);
    transition:
      opacity 180ms ease,
      transform 180ms ease,
      visibility 180ms ease;
  }

  .nav-item.open .dropdown {
    visibility: visible;
    opacity: 1;
    transform: translate(-50%, 0);
  }

  .desktop-media-nav .dropdown {
    right: 0;
    left: auto;
    transform: translateY(8px);
  }

  .desktop-media-nav .nav-item.open .dropdown {
    transform: translateY(0);
  }

  .dropdown-link {
    display: block;
    width: 100%;
    min-height: 40px;
    padding: 9px 12px;
    color: var(--ink);
    text-align: left;
    cursor: pointer;
    background: transparent;
    border: 0;
    border-radius: 6px;
  }

  .dropdown-link:hover {
    color: var(--brand);
    background: var(--surface-soft);
  }

  .dropdown-link.active {
    font-weight: 600;
    color: var(--brand);
    background: var(--surface-soft);
  }

  .external-mark {
    font-size: 13px;
    color: currentColor;
  }

  .mobile-menu-button {
    display: none;
    width: 46px;
    height: 46px;
    padding-left: 24px;
    color: var(--brand);
    cursor: pointer;
    background: transparent;
    border: 0;
  }

  .mobile-menu-button span {
    display: block;
    height: 2px;
    margin: 5px 0;
    background: currentColor;
    transition:
      transform 180ms ease,
      opacity 180ms ease;
  }

  .mobile-menu-button[aria-expanded='true'] span:nth-child(1) {
    transform: translateY(7px) rotate(45deg);
  }

  .mobile-menu-button[aria-expanded='true'] span:nth-child(2) {
    opacity: 0;
  }

  .mobile-menu-button[aria-expanded='true'] span:nth-child(3) {
    transform: translateY(-7px) rotate(-45deg);
  }

  .mobile-drawer {
    position: fixed;
    inset: 74px 0 0;
    z-index: 38;
    padding: 12px 20px calc(32px + env(safe-area-inset-bottom));
    overflow-y: auto;
    background: var(--surface);
  }

  .mobile-tool-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    padding: 4px 0 12px;
    border-bottom: 1px solid var(--line);
  }

  .mobile-tool-action {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    color: var(--ink-muted);
    cursor: pointer;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 8px;
  }

  .mobile-tool-action:hover {
    color: var(--brand);
    border-color: var(--brand);
  }

  .mobile-nav-row {
    border-bottom: 1px solid var(--line);
  }

  .mobile-media-nav .mobile-subnav.open {
    max-height: none;
  }

  .mobile-nav-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 52px;
  }

  .mobile-nav-link,
  .mobile-nav-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 52px;
    font-size: 15px;
    font-weight: 500;
    color: var(--ink);
    text-align: left;
    cursor: pointer;
    background: none;
    border: 0;
  }

  .mobile-nav-row.active > .mobile-nav-main .mobile-nav-link,
  .mobile-nav-row.active > .mobile-nav-main .mobile-nav-trigger {
    font-weight: 600;
    color: var(--brand);
  }

  .mobile-subnav {
    max-height: 0;
    overflow: hidden;
    transition: max-height 280ms ease;
  }

  .mobile-subnav.open {
    max-height: 480px;
  }

  .mobile-subnav-link {
    display: block;
    min-height: 44px;
    padding: 8px 12px;
    color: var(--ink-muted);
    text-align: left;
    cursor: pointer;
    background: none;
    border: 0;
  }

  .mobile-subnav-link:hover {
    color: var(--brand);
  }

  .mobile-subnav-link.active {
    font-weight: 600;
    color: var(--brand);
    background: var(--surface-soft);
  }

  @media (max-width: 767.98px) {
    .nav-band {
      display: none;
    }

    .header-actions {
      display: none;
    }

    .mobile-menu-button {
      display: block;
    }

    .brand-en {
      max-width: 210px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .brand-cn {
      font-size: clamp(12px, 3.4vw, 14px);
      white-space: nowrap;
    }
  }

  @media (max-width: 479.98px) {
    .brand-en {
      display: none;
    }

    .brand-logo {
      flex: 0 0 43px;
      width: 43px;
      height: 43px;
    }
  }

  :focus-visible {
    outline: 3px solid color-mix(in oklch, var(--brand), white 35%);
    outline-offset: 2px;
  }
</style>
